import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Grid,
  TextField,
  Typography,
  Container,
  IconButton,
  Checkbox,
  Box,
  InputAdornment,
  Alert,
  Fade,
} from '@mui/material'
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18n from '../../i18n'
import { clearMessageError, signUp } from '../../redux/actions/authAction'
import CustomButton from '../../components/CustomButton'
import LanguageSelector from '../../components/LanguageSelector'
import { isMobile } from '../../utils/utils'
import { landingUrl } from '../../config'
import { tracker } from '../../analytics'

const GOLD = '#c9a96e'

const fadeInUpKeyframes = {
  '@keyframes fadeInUp': {
    from: { opacity: 0, transform: 'translateY(24px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
}

const stagger = (index) => ({
  opacity: 0,
  animation: 'fadeInUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards',
  animationDelay: `${index * 0.08}s`,
})

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    fontFamily: '"DM Sans", sans-serif',
    fontSize: '15px',
    color: '#fff',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.08)',
      transition: 'border-color 0.2s ease',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.16)',
    },
    '&.Mui-focused fieldset': {
      borderColor: GOLD,
      borderWidth: '1.5px',
    },
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
      WebkitBoxShadow: '0 0 0 1000px #111111 inset',
      WebkitTextFillColor: '#fff',
      caretColor: '#fff',
      borderRadius: '12px',
      transition: 'background-color 5000s ease-in-out 0s',
    },
  },
  '& .MuiInputLabel-root': {
    fontFamily: '"DM Sans", sans-serif',
    color: 'rgba(255, 255, 255, 0.35)',
    fontSize: '14px',
    '&.Mui-focused': {
      color: GOLD,
    },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: '"DM Sans", sans-serif',
  },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: 'rgba(255, 255, 255, 0.25)',
    fontSize: '20px',
  },
}

const PLAN_LABELS = { starter: 'Starter', pro: 'Pro' }

const SignupScreen = () => {
  const dispatch = useDispatch()
  const { loading, error, user } = useSelector(state => state.auth)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const [loadingButton, setLoadingButton] = useState(false)

  // Read plan/billing/region/lang/quantity/currency from URL (set by landing page CTAs)
  const planKey = searchParams.get('plan') || null
  const billingCycle = searchParams.get('billing') || 'annual'
  const region = searchParams.get('region') || 'default'
  const lang = searchParams.get('lang') || null
  const quantity = parseInt(searchParams.get('quantity') || '1', 10) || 1
  const currency = (searchParams.get('currency') || '').toUpperCase() || null

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [acceptTerms, setAcceptTerms] = useState(false)

  const [emailError, setEmailError] = useState(false)
  const [passwordError, setPasswordError] = useState(false)
  const [confirmPasswordError, setConfirmPasswordError] = useState(false)
  const isMobileView = isMobile()

  const handleSignup = async () => {
    setLoadingButton(true)

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    const isValidPassword = password.length >= 8 && /[A-Z]/.test(password) && /[a-z]/.test(password) && /\d/.test(password)
    const isPasswordMatch = password === confirmPassword

    setPasswordError(!isValidPassword)
    setConfirmPasswordError(!isPasswordMatch)

    if (!isValidEmail) {
      setEmailError(t('auth_email_invalid'))
    } else {
      setEmailError(false)
    }

    if (isValidEmail && isValidPassword && isPasswordMatch && acceptTerms) {
      tracker.conversion({ category: 'signup_submitted', action: 'submit', properties: { plan: planKey, billing_cycle: billingCycle, region } })
      // Store plan preference for later use when user subscribes (after trial)
      if (planKey) {
        localStorage.setItem('selectedPlan', JSON.stringify({ plan: planKey, billing: billingCycle, region, lang: lang || 'en', quantity, currency }))
      }
      dispatch(signUp(email.toLowerCase(), password, navigate))
    }

    setLoadingButton(false)
  }

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSignup()
    }
  }

  // Sync language from landing page URL param
  useEffect(() => {
    if (lang && ['en', 'fr', 'ar'].includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang)
    }
  }, [lang])

  // No longer require plan selection — users can sign up for a free trial without choosing a plan

  useEffect(() => {
    if (user) {
      navigate('/')
    }
  }, [user, navigate])

  useEffect(() => {
    dispatch(clearMessageError())
    tracker.conversion({ category: 'signup_form_loaded', action: 'view', properties: { plan: planKey, billing_cycle: billingCycle, region } })
  }, []) // eslint-disable-line

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#000',
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        ...fadeInUpKeyframes,
      }}
    >
      {/* Grain texture overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.025,
          pointerEvents: 'none',
          zIndex: 1,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '256px 256px',
        }}
      />

      {/* Language selector */}
      <Box sx={{ position: 'absolute', top: 20, right: 24, zIndex: 10 }}>
        <LanguageSelector variant="dark" />
      </Box>

      <Grid container sx={{ minHeight: '100vh', position: 'relative', zIndex: 2 }}>
        {/* Left Column — Branding */}
        {!isMobileView && (
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              background: '#000',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              padding: 6,
            }}
          >
            {/* Radial gold glow */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(201, 169, 110, 0.05) 0%, transparent 70%)',
                pointerEvents: 'none',
              }}
            />

            {/* Subtle grid overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.025,
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                backgroundSize: '80px 80px',
                pointerEvents: 'none',
              }}
            />

            {/* Logo */}
            <Box sx={{ ...stagger(0), mb: 5 }}>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '1px solid rgba(255,255,255,0.06)',
                  backgroundColor: 'rgba(255,255,255,0.02)',
                  overflow: 'hidden',
                }}
              >
                <img
                  src="/logo/logo-circle-dark-mode.svg"
                  alt="Lawis Logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    padding: '10px',
                  }}
                />
              </Box>
            </Box>

            {/* Heading */}
            <Typography
              sx={{
                ...stagger(1),
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 300,
                fontSize: 'clamp(2rem, 3.5vw, 3.2rem)',
                color: '#fff',
                textAlign: 'center',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                mb: 2.5,
                zIndex: 2,
                position: 'relative',
              }}
            >
              {t('auth_join_title')}
            </Typography>

            {/* Subtitle */}
            <Typography
              sx={{
                ...stagger(2),
                fontFamily: '"DM Sans", sans-serif',
                fontWeight: 400,
                fontSize: '15px',
                color: 'rgba(255, 255, 255, 0.40)',
                textAlign: 'center',
                maxWidth: '380px',
                lineHeight: 1.7,
                zIndex: 2,
                position: 'relative',
              }}
            >
              {t('auth_join_subtitle')}
            </Typography>

            {/* Decorative gold line */}
            <Box
              sx={{
                ...stagger(3),
                width: '48px',
                height: '1.5px',
                backgroundColor: GOLD,
                opacity: 0.4,
                mt: 4,
                borderRadius: '1px',
              }}
            />
          </Grid>
        )}

        {/* Right Column — Signup Form */}
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            backgroundColor: '#0d0d0d',
            borderLeft: isMobileView ? 'none' : '1px solid rgba(255, 255, 255, 0.04)',
            position: 'relative',
            overflowY: 'auto',
            maxHeight: '100vh',
            py: 4,
          }}
        >
          {/* Mobile logo */}
          {isMobileView && (
            <Box sx={{ ...stagger(0), mb: 3, mt: 2 }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '1px solid rgba(255,255,255,0.06)',
                  backgroundColor: 'rgba(255,255,255,0.02)',
                  overflow: 'hidden',
                }}
              >
                <img
                  src="/logo/logo-circle-dark-mode.svg"
                  alt="Lawis Logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    padding: '8px',
                  }}
                />
              </Box>
            </Box>
          )}

          <Container maxWidth="sm" sx={{ maxWidth: '520px !important', px: isMobileView ? 3 : 4 }}>
            {/* Section label */}
            <Typography
              sx={{
                ...stagger(0),
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: 'rgba(255, 255, 255, 0.25)',
                mb: 2,
              }}
            >
              {t('auth_signup_title')}
            </Typography>

            {/* Main heading */}
            <Typography
              sx={{
                ...stagger(1),
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 300,
                fontSize: isMobileView ? '1.8rem' : '2.4rem',
                color: '#fff',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                mb: 1,
              }}
            >
              {t('auth_signup_subtitle')}
            </Typography>

            {/* Decorative line */}
            <Box
              sx={{
                ...stagger(2),
                width: '32px',
                height: '1.5px',
                backgroundColor: GOLD,
                opacity: 0.5,
                mb: 3.5,
                mt: 2,
                borderRadius: '1px',
              }}
            />

            {/* No credit card badge */}
            <Box
              sx={{
                ...stagger(2),
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: planKey ? 1.5 : 3,
                px: 2,
                py: 1,
                borderRadius: '8px',
                backgroundColor: 'rgba(34,197,94,0.06)',
                border: '1px solid rgba(34,197,94,0.15)',
                width: 'fit-content',
              }}
            >
              <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '12px', color: 'rgba(34,197,94,0.9)', fontWeight: 600 }}>
                &#10003; {t('auth_no_credit_card', 'Essai de 7 jours \u2014 aucune carte bancaire requise')}
              </Typography>
            </Box>

            {/* Plan badge — shown when coming from landing page CTA */}
            {planKey && (
              <Box
                sx={{
                  ...stagger(2),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 3,
                  px: 2,
                  py: 1.5,
                  borderRadius: '10px',
                  backgroundColor: 'rgba(201,169,110,0.07)',
                  border: '1px solid rgba(201,169,110,0.2)',
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: GOLD,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.55)' }}>
                  {t('auth_signup_plan_summary_before')}{' '}
                  <Typography component="span" sx={{ color: GOLD, fontWeight: 700, fontFamily: 'inherit', fontSize: 'inherit' }}>
                    {PLAN_LABELS[planKey] || planKey}
                  </Typography>
                  {' '}{t('auth_signup_plan_summary_after', { billing: t(billingCycle) })}
                </Typography>
              </Box>
            )}

            {/* Error alert */}
            {error && (
              <Fade in={!!error}>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: '12px',
                    fontFamily: '"DM Sans", sans-serif',
                    backgroundColor: 'rgba(239, 68, 68, 0.08)',
                    color: '#fca5a5',
                    border: '1px solid rgba(239, 68, 68, 0.15)',
                    '& .MuiAlert-icon': {
                      color: '#f87171',
                    },
                  }}
                >
                  {error}
                </Alert>
              </Fade>
            )}

            {/* Form fields */}
            <Box sx={stagger(3)}>
              <Grid container spacing={2}>
                {/* Email */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('auth_email_label')}
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value)
                      setEmailError(false)
                    }}
                    error={!!emailError}
                    helperText={emailError}
                    required
                    onKeyPress={handleKeyPress}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email />
                        </InputAdornment>
                      )
                    }}
                    sx={inputSx}
                  />
                </Grid>

                {/* Password */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('auth_password_label')}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    error={passwordError}
                    helperText={
                      passwordError ? t('auth_password_requirements') : ''
                    }
                    required
                    onKeyPress={handleKeyPress}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: 'rgba(255,255,255,0.25)' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={inputSx}
                  />
                </Grid>

                {/* Confirm Password */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('auth_confirm_password')}
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => { setConfirmPassword(e.target.value); setConfirmPasswordError(false) }}
                    error={confirmPasswordError}
                    helperText={confirmPasswordError ? t('auth_passwords_not_match') : ''}
                    required
                    onKeyPress={handleKeyPress}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: 'rgba(255,255,255,0.25)' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={inputSx}
                  />
                </Grid>

                {/* Terms and Conditions */}
                <Grid item xs={12} sx={{ mt: 0.5 }}>
                  <Box display="flex" alignItems="center">
                    <Checkbox
                      checked={acceptTerms}
                      onChange={e => setAcceptTerms(e.target.checked)}
                      required
                      sx={{
                        color: 'rgba(255, 255, 255, 0.2)',
                        '&.Mui-checked': {
                          color: GOLD,
                        },
                      }}
                    />
                    <Typography
                      sx={{
                        color: '#f87171',
                        marginRight: '5px',
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: '14px',
                      }}
                    >
                      *
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: '13px',
                        color: 'rgba(255, 255, 255, 0.45)',
                      }}
                    >
                      {t('auth_accept_terms')}{' '}
                      <Typography
                        component="a"
                        href="https://www.lawis.ai/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontFamily: '"DM Sans", sans-serif',
                          fontSize: '13px',
                          color: GOLD,
                          fontWeight: 600,
                          textDecoration: 'none',
                          transition: 'opacity 0.2s ease',
                          '&:hover': {
                            opacity: 0.8,
                          },
                        }}
                      >
                        {t('auth_terms_conditions')}
                      </Typography>
                    </Typography>
                  </Box>
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <CustomButton
                    text={t('auth_signup_button')}
                    outlined={false}
                    loading={loading || loadingButton}
                    onClick={handleSignup}
                    disabled={loading || loadingButton || !acceptTerms}
                    sx={{
                      width: '100%',
                      py: 1.6,
                      borderRadius: '12px',
                      backgroundColor: '#fff',
                      color: '#000',
                      fontFamily: '"DM Sans", sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      textTransform: 'none',
                      boxShadow: 'none',
                      transition: 'all 0.2s ease',
                      ':hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.88)',
                        boxShadow: 'none',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        color: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Divider */}
            <Box
              sx={{
                ...stagger(4),
                display: 'flex',
                alignItems: 'center',
                my: 3,
              }}
            >
              <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.06)' }} />
              <Typography
                sx={{
                  px: 2.5,
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                }}
              >
                {t('auth_or')}
              </Typography>
              <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.06)' }} />
            </Box>

            {/* Login link */}
            <Box sx={{ ...stagger(5), textAlign: 'center', mb: 4 }}>
              <Typography
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.35)',
                }}
              >
                {t('auth_have_account')}{' '}
                <Typography
                  component="span"
                  onClick={() => navigate('/login')}
                  sx={{
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: '14px',
                    color: GOLD,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'opacity 0.2s ease',
                    '&:hover': {
                      opacity: 0.8,
                    },
                  }}
                >
                  {t('auth_login_link')}
                </Typography>
              </Typography>
            </Box>
          </Container>
        </Grid>
      </Grid>
    </Box>
  )
}

export default SignupScreen
